export function normalizeHsk(value) {
  return String(value || "").replace(/\D/g, "");
}

/**
 * 공백·대소문자·양허유형 꼬리표를 무시한 비교용 이름.
 *
 * <p>`customs_hs_master.name_ko` 에는 「방사선 방호복/양허유형;0」처럼 양허유형 표시가 붙어 있다.
 * 이것 때문에 「방사선방호복」이 공식명 정확일치에 걸리지 않고, 그래서 4015 하나로 지목될 수
 * 있는 물품이 4015·5603·6206·6211 네 호를 고르라는 질문으로 떨어졌다.
 */
function normalizeName(value) {
  return String(value || "")
    .replace(/\/\s*양허유형\s*;?\s*[0-9]*/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/** 화면 표시용 — 양허유형 꼬리표만 떼고 원문은 유지한다. */
export function stripTariffMeta(value) {
  return String(value || "").replace(/\/\s*양허유형\s*;?\s*[0-9]*/g, "").trim();
}

/**
 * 관세율표 공식명이 검색어와 정확히 일치하는 세번들.
 *
 * <p>이것이 있으면 다른 어떤 신호보다 강하다. 「파인애플」은 0804.30(신선·건조)·2006.00(설탕절임)·
 * 2008.20(조제·저장)의 공식명이며, 진짜 질문은 가공 상태 하나다. 그런데 선례에는 파인애플
 * 아이스바(2106)·비누(3401)·축제용품(9505)처럼 **파인애플을 향이나 모양으로 언급한 제품**이
 * 섞여 있어, 그것까지 후보로 담으면 "제2부·제4부·제6부·제7부·제13부·제20부 중 무엇입니까"라는
 * 답할 수 없는 질문이 만들어진다. 향으로 언급된 물품은 그 물품을 분류하는 후보가 아니다.
 */
export function exactOfficialNameCandidates(query, tariffItems) {
  const target = normalizeName(query);
  if (target.length < 2) return [];
  const out = [];
  const seen = new Set();
  for (const item of tariffItems || []) {
    const name = normalizeName(item && (item.desc_ko || item.descKo || item.desc));
    if (name !== target) continue;
    const hsk = normalizeHsk(item.hsk);
    if (hsk.length < 4 || seen.has(hsk)) continue;
    seen.add(hsk);
    out.push({ hsk, label: String(item.desc_ko || item.descKo || item.desc || "") });
  }
  return out;
}

/**
 * Build a diverse HS4 entry set from the evidence already retrieved by unified search.
 *
 * <p>공식명 정확일치가 있으면 그 세번들만 쓴다. 정확일치가 너무 많으면(6개 초과) 그 이름이
 * 변별력이 없다는 뜻이므로 기존 수집으로 돌아간다.
 */
export function collectRefinementCandidates(data, limit = 24, query = "") {
  const tariffItems = (data && data.tariff && data.tariff.items) || [];
  const exact = exactOfficialNameCandidates(query, tariffItems);
  if (exact.length > 0 && exact.length <= 6) {
    return dedupeByHeading(exact).slice(0, limit);
  }
  // 관세율표 매칭을 앞에 두고, 부(상품군)가 일정 수를 넘기 시작하면 그 뒤는 버린다.
  //
  // 관세율표는 그 말이 **무엇의 이름인지**를 말하고, 선례는 **그 말을 언급한 다른 물품**을
  // 말한다. 「돼지」는 관세율표에서 0206.30 돼지의 것·0502.10 돼지털·0511.99 돼지정액으로
  // 모두 제1부에 모이는데, 선례를 함께 담으면 영문 `pig` 부분일치로 7202(pig iron)·
  // PIGMENTS·9018(의료기기)이 들어와 "제1·2·3·4·6·8·15·18부 중 무엇입니까"가 된다.
  //
  // 그렇다고 선례를 아예 빼면 recall 이 깎인다 — 물티슈처럼 부직포(제11부)와 플라스틱시트
  // (제7부)에 정답이 걸쳐 있는 경우가 실제로 있다. 그래서 배제가 아니라 상한이다: 순서를
  // 관세율표 → 호 표제 → 선례로 두고, 부가 상한을 넘기 시작하는 지점에서 끊는다.
  const ordered = [];
  for (const item of tariffItems) {
    ordered.push({ hsk: normalizeHsk(item.hsk), label: String(item.desc_ko || item.descKo || item.desc || "") });
  }
  for (const match of (data && data.tariff && data.tariff.headingMatches) || []) {
    ordered.push({ hsk: normalizeHsk(match.code), label: String(match.line_ko || match.lineKo || "") });
  }
  for (const item of [...((data && data.cases) || []), ...((data && data.rulings) || [])]) {
    ordered.push({ hsk: normalizeHsk(item && (item.hsk || item.hs6)), label: String((item && item.label) || "") });
  }
  return capSections(dedupeByHeading(ordered.filter((row) => row.hsk.length >= 4))).slice(0, limit);
}

/** 부(2자리 류를 부로 대용)가 상한을 넘기 시작하면 그 뒤는 버린다. */
function capSections(rows, maxSections = 3) {
  const chapters = new Set();
  const out = [];
  for (const row of rows) {
    const chapter = row.hsk.slice(0, 2);
    if (!chapters.has(chapter) && chapters.size >= maxSections) continue;
    chapters.add(chapter);
    out.push(row);
  }
  return out;
}

function dedupeByHeading(rows) {
  const headings = new Set();
  const out = [];
  for (const row of rows) {
    const hs4 = row.hsk.slice(0, 4);
    if (headings.has(hs4)) continue;
    headings.add(hs4);
    out.push(row);
  }
  return out;
}

/** 수집된 모든 증거가 걸쳐 있는 류 수 — **상한 적용 전** 값이다. */
export function evidenceBreadth(data) {
  const chapters = new Set();
  const push = (value) => {
    const digits = normalizeHsk(value);
    if (digits.length >= 4) chapters.add(digits.slice(0, 2));
  };
  for (const item of (data && data.tariff && data.tariff.items) || []) push(item.hsk);
  for (const match of (data && data.tariff && data.tariff.headingMatches) || []) push(match.code);
  for (const item of [...((data && data.cases) || []), ...((data && data.rulings) || [])]) {
    push(item && (item.hsk || item.hs6));
  }
  return chapters.size;
}

/** 관세율표가 이 검색어를 실제로 아는가 — 공식명 정확일치 또는 세번 직접 매칭. */
export function hasTariffAnchor(data, query) {
  const items = (data && data.tariff && data.tariff.items) || [];
  if (items.length > 0) return true;
  return exactOfficialNameCandidates(query, items).length > 0;
}

/**
 * 부를 고르라는 질문을 낼 자격이 없어, 품명을 더 받아야 하는 상태인가.
 *
 * <p>판정은 **넓이가 아니라 앵커**다. 처음에는 "증거가 N개 류를 넘으면 넓다"로 잡았는데 틀렸다 —
 * 「돼지」는 관세율표가 0206·0209·0502·0511로 제대로 짚고 있는데도 선례에 pig iron·PIGMENTS·
 * 의료기기가 붙어 원시 넓이가 6개 류가 된다. 넓이로 재면 답할 수 있는 질문까지 막힌다.
 *
 * <p>가르는 것은 **관세율표가 이 말을 아는지**다. 「말」은 한 글자라 토크나이저가 버려 관세율표
 * 매칭이 하나도 없고, 남는 것은 그 말을 언급한 다른 물품들 — 말의 심장·마용 사료·말용 샴푸·
 * 말그림 액자 — 뿐이다. 여기서 뽑은 부 목록은 **그 물품의 상품군이 아니라 그 단어를 언급한
 * 물품들의 상품군**이며, 그래서 화학공업(말용 샴푸 3307)이 선택지로 올라온다. 말은 화학공업
 * 생산품이 아니다.
 */
export function needsMoreSpecificName(data, query) {
  return !hasTariffAnchor(data, query);
}

/**
 * 호 선택지 라벨을 검색어 중심으로 다시 세운다.
 *
 * <p>제0804호의 표제는 「대추야자ㆍ무화과ㆍ파인애플ㆍ아보카도ㆍ구아바ㆍ망고ㆍ망고스틴(신선하거나
 * 건조한 것으로 한정한다)」이다. 파인애플을 찾는 사람에게 **첫 단어가 대추야자인 라벨**을 주는 것은
 * 읽을 수 없는 화면이다. 게다가 서버가 46자에서 자르는 탓에 정작 셋을 가르는 「신선하거나 건조한
 * 것으로 한정한다」가 잘려 사라진다 — 형제 과일 목록은 남고 구분자는 버려진다.
 *
 * <p>그래서 열거 중 검색어에 해당하는 항목을 앞으로 끌어내고(「파인애플 등」), 잘려나간 한정 문구는
 * 검색이 이미 갖고 있는 호 표제 전문에서 되살려 부제로 붙인다. 법문을 고치는 것이 아니라 읽는
 * 순서를 고치는 것이며, 전문은 그대로 함께 보여준다.
 */
export function condenseHeadingLabel(labelKo, query) {
  const raw = String(labelKo || "");
  const codeMatch = raw.match(/\s*\((\d{4})\)\s*$/);
  const code = codeMatch ? codeMatch[1] : "";
  const body = codeMatch ? raw.slice(0, codeMatch.index) : raw;
  const target = normalizeName(query);
  if (target.length < 2 || !body.includes("ㆍ")) {
    return { lead: body.trim(), code, condensed: false };
  }
  const parts = body.split("ㆍ");
  const hit = parts.find((part) => normalizeName(part).includes(target));
  if (!hit) return { lead: body.trim(), code, condensed: false };
  return { lead: `${hit.trim()} 등`, code, condensed: true };
}

/** 호 표제 전문에서 「(…으로 한정한다)」 같은 한정 문구만 뽑는다. 없으면 빈 문자열. */
export function headingQualifier(headingMatches, hs4) {
  for (const match of headingMatches || []) {
    if (normalizeHsk(match && (match.hs4 || match.code)).slice(0, 4) !== normalizeHsk(hs4)) continue;
    const line = String(match.line_ko || match.lineKo || "");
    const tail = line.match(/\(([^()]*(?:한정한다|제외한다|포함한다)[^()]*)\)\s*$/);
    if (tail) return tail[1].trim();
  }
  return "";
}

/** 사전심사·관세청 결정례 — 근거로 쓰는 1차 자료. */
const TIER1_SOURCES = new Set(["clip_kcs", "pre_ruling", "kcs_pre_ruling"]);

/**
 * 검색이 이미 가져온 증거만으로 공식 경로를 미리 채워볼 세번을 고른다.
 *
 * 여기서 고른 값은 **제안일 뿐**이다. 서버는 그 세번까지의 경로를 보여주기만 하고, 사용자가
 * 확정하기 전에는 아무것도 적용하지 않으며 기록도 selection_only 로 남는다. 그래서 신고이력만
 * 있는 경우에도 제안은 할 수 있다 — 다만 무엇에 근거했는지를 화면에 그대로 적는다.
 *
 * Tier1(사전심사·관세청 결정례)이 하나로 모이면 그것을 쓴다. Tier1 이 없으면 Tier2(신고이력·
 * 해외사례)를 보되, 3건 이상이 80% 넘게 한 세번으로 모일 때만 제안한다. 신고이력이 갈리는데
 * 최빈값을 들이미는 것은 폐기한 __most_common__ 과 같은 짓이 된다.
 */
export function proposeFromSearchEvidence(cases, rulings, exactMatches = []) {
  // 공식명이 정확히 일치하는 세번이 하나면 그게 가장 강한 근거다. 「경주말」은 0101.29-1000,
  // 「커피분쇄기」는 8509.80-1000 의 공식명 그 자체다 — 선례가 없어도 물어볼 것이 없다.
  const exactTen = [...new Set((exactMatches || [])
    .map((row) => normalizeHsk(row && row.hsk))
    .filter((hsk) => hsk.length === 10))];
  if (exactTen.length === 1) {
    return { hsk: exactTen[0], basis: "exact_name", count: 1, total: 1 };
  }

  const tier1 = new Map();
  const tier2 = new Map();
  for (const item of [...(cases || []), ...(rulings || [])]) {
    const hsk = normalizeHsk(item && (item.hsk || item.decided_hsk_10digit));
    if (hsk.length !== 10) continue;
    const bucket = TIER1_SOURCES.has(item.source) ? tier1 : tier2;
    bucket.set(hsk, (bucket.get(hsk) || 0) + 1);
  }

  if (tier1.size === 1) {
    const [hsk, count] = [...tier1.entries()][0];
    return { hsk, basis: "tier1", count, total: count };
  }
  if (tier1.size > 1) return null; // 1차 자료가 갈리면 사람이 봐야 한다.

  const total = [...tier2.values()].reduce((sum, n) => sum + n, 0);
  if (total < 3) return null;
  const [top] = [...tier2.entries()].sort((a, b) => b[1] - a[1]);
  if (!top || top[1] / total < 0.8) return null;
  return { hsk: top[0], basis: "tier2", count: top[1], total };
}

/** 제안 근거를 화면 문구로. 무엇에 기대고 있는지 숨기지 않는다. */
export function describeProposalBasis(proposal) {
  if (!proposal) return "";
  if (proposal.basis === "exact_name") {
    return "관세율표 공식품명이 검색어와 정확히 일치하는 유일한 세번입니다.";
  }
  return proposal.basis === "tier1"
    ? `사전심사·관세청 결정례 ${proposal.count}건이 이 세번입니다.`
    : `관세사 신고사례 ${proposal.count}/${proposal.total}건이 이 세번입니다. 사전심사·결정례는 없습니다.`;
}

export function formatHsk(value) {
  const h = normalizeHsk(value);
  if (h.length >= 10) return `${h.slice(0, 4)}.${h.slice(4, 6)}-${h.slice(6, 10)}`;
  if (h.length >= 6) return `${h.slice(0, 4)}.${h.slice(4, 6)}`;
  return h;
}

export function refinementStorageKey(query) {
  return `loginhs:search-refinement:${String(query || "").trim()}`;
}

const FACT_LABELS = {
  wire_configuration: "전선 형태",
  product_category: "물품 종류·기능",
  material_base: "주된 재질",
  construction: "제품 구조",
  article_character: "제품 성격",
  use: "주된 용도",
};

const FACT_VALUES = {
  connector_fitted: "커넥터 부착 · 8544.42",
  connector_not_fitted: "커넥터 미부착 · 8544.49",
  vehicle_wiring_set: "차량용 배선세트 · 8544.30",
  coaxial: "동축케이블 · 8544.20",
  optical_fiber: "광섬유 케이블 · 8544.70",
  winding_wire: "권선용 전선 · 8544.11/19",
  electrical_conductor: "절연 전선·케이블",
  connector: "플러그·소켓·커넥터",
  control_panel: "제어반·제어모듈",
  power_converter: "정류기·전원변환기",
  electronic_module: "그 밖의 전자장치·모듈",
  measuring_instrument: "측정·검사용 기기",
};

export function formatRefinementFact(key, value) {
  return `${FACT_LABELS[key] || key}: ${FACT_VALUES[value] || String(value)}`;
}

/** Normalize the Spring API's snake_case payload at the frontend boundary. */
export function normalizeRefinementResult(payload) {
  if (!payload || typeof payload !== "object") return null;
  const question = payload.question && typeof payload.question === "object"
    ? {
        ...payload.question,
        factKey: payload.question.factKey || payload.question.fact_key || "",
        affectsHsks: payload.question.affectsHsks || payload.question.affects_hsks || [],
        options: (payload.question.options || []).map((option) => ({
          ...option,
          candidateHsks: option.candidateHsks || option.candidate_hsks || [],
        })),
      }
    : null;

  return {
    ...payload,
    knownFacts: payload.knownFacts || payload.known_facts || {},
    definitionCoverage: payload.definitionCoverage ?? payload.definition_coverage ?? 0,
    candidateCount: payload.candidateCount ?? payload.candidate_count ?? 0,
    assessments: (payload.assessments || []).map((assessment) => ({
      ...assessment,
      validatedPrefix: assessment.validatedPrefix || assessment.validated_prefix || null,
      definitionAvailable: assessment.definitionAvailable
        ?? assessment.definition_available
        ?? false,
    })),
    question,
  };
}
